import React, { useState, useEffect, useReducer, useCallback, useRef } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { logger } from './lib/logger';

// Layout Components
import BreadflowIcon from './components/layout/BreadflowIcon.tsx';
import { Header } from './components/layout/Header';
import { Navigation } from './components/layout/Navigation';
import { EmailVerificationBanner } from './components/auth/EmailVerificationBanner';
import { SubscriptionBanner } from './components/subscription/SubscriptionBanner';
import { NotificationErrorBanner } from './components/notifications/NotificationErrorBanner';

// Page Components
import { Home } from './pages/Home';
import { StarterPage } from './components/starter/StarterPage';
import { ChallengesPage } from './components/challenges/ChallengesPage';
import { AnalysisPage } from './components/analysis/AnalysisPage';
import { KnowledgePage } from './pages/KnowledgePage';
import { NewBakeModal } from './components/baking/NewBakeModal';

// Auth Components
import { AuthModal } from './components/auth/AuthModal';

// Onboarding Components
import { WelcomeModal } from './components/onboarding/WelcomeModal';
import { HelpCenterModal } from './components/help/HelpCenterModal';
import { QRScanSuccessModal } from './components/qr/QRScanSuccessModal';
import { QRScanStatusOverlay } from './components/qr/QRScanStatusOverlay';
import { QRCodeGeneratorModal } from './components/qr/QRCodeGeneratorModal';
import { InstantScanFeedback } from './components/qr/InstantScanFeedback';
import { WebViewGoldDebug } from './components/debug/WebViewGoldDebug';

// Firebase and Services
import { auth } from './lib/firebase';
import { addBakingSession } from './lib/store';
import { initializeNotifications } from './lib/notifications';
import { checkAndUpdateSubscriptionStatus } from './lib/auth';
import { doc, getDoc, getDocFromServer, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './lib/firebase'; 

// Hooks and Utils
import { useStarters, useBakingSessions } from './lib/hooks';
import { useTokenPrefetch } from './hooks/useTokenPrefetch';
import { StarterDetailsModal } from './components/starter/StarterDetailsModal';
import { StarterActivityGraph } from './components/starter/StarterActivityGraph';
import { updateStarter, addStarter } from './lib/store';
import { NewStarterModal } from './components/starter/NewStarterModal';
import { BakingInProgressWidget } from './components/baking/BakingInProgressWidget';
import { checkStarterHealth, checkAndNotifyStarterFeeding } from './lib/utils';
import { SubscriptionProvider, useSubscription } from './components/subscription/SubscriptionContext';
import { SubscriptionModal } from './components/subscription/SubscriptionModal';
import { PremiumFeature } from './components/subscription/PremiumFeature';
import { FERMENTATION_PROCESSES } from './types/baking';
import { shouldBypassSubscription, isIOSWebViewGold } from './lib/platform';

type QRScanStatus = 'idle' | 'scanning' | 'success' | 'timeout';

interface QRScanState {
  showSuccessModal: boolean;
  status: QRScanStatus;
  scannedId: string | null;
}

type QRScanAction =
  | { type: 'SCAN_START' }
  | { type: 'SCAN_SUCCESS'; payload: string }
  | { type: 'SCAN_CANCEL' }
  | { type: 'RESET_SCAN' };

function qrScanReducer(state: QRScanState, action: QRScanAction): QRScanState {
  switch (action.type) {
    case 'SCAN_START':
      return {
        showSuccessModal: false,
        status: 'scanning',
        scannedId: null,
      };
    case 'SCAN_SUCCESS':
      return {
        showSuccessModal: true,
        status: 'success',
        scannedId: action.payload,
      };
    case 'SCAN_CANCEL':
      return {
        showSuccessModal: false,
        status: 'idle',
        scannedId: null,
      };
    case 'RESET_SCAN':
      return {
        showSuccessModal: false,
        status: 'idle',
        scannedId: null,
      };
    default:
      return state;
  }
}

function AppContent() {
  logger.info('AppContent rendering', { component: 'AppContent' });

  const { prefetchState, token: prefetchToken, retryPrefetch } = useTokenPrefetch();

  const [user, loading, error] = useAuthState(auth);

  // Force service worker update and clear cache on load
  useEffect(() => {
    const updateServiceWorker = async () => {
      try {
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            await registration.update();
            console.log('Service worker updated');
          }
        }
      } catch (error) {
        console.error('Error updating service worker:', error);
      }
    };

    updateServiceWorker();
  }, []);

  useEffect(() => {
    logger.info('Auth state changed', {
      component: 'AppContent',
      loading,
      hasUser: !!user,
      userId: user?.uid,
      userEmail: user?.email,
      hasError: !!error,
      errorMessage: error?.message,
      authCurrentUser: !!auth.currentUser,
      authCurrentUserId: auth.currentUser?.uid
    });
  }, [user, loading, error]);

  const [activeTab, setActiveTab] = useState('home');
  const [showNewBake, setShowNewBake] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [showSubscriptionBanner, setShowSubscriptionBanner] = useState(false);
  const [daysLeftInTrial, setDaysLeftInTrial] = useState<number | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [activeSession, setActiveSession] = useState<BakingSession | null>(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showNewStarter, setShowNewStarter] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [showQRGenerator, setShowQRGenerator] = useState(false);
  const [showInstantFeedback, setShowInstantFeedback] = useState(false);
  const [notificationsChecked, setNotificationsChecked] = useState(false);

  const [qrScanState, dispatchQRScan] = useReducer(qrScanReducer, {
    showSuccessModal: false,
    status: 'idle',
    scannedId: null,
  });

  const lastProcessedQR = useRef<string | null>(null);
  const scanSessionId = useRef<string | null>(null);
  const isProcessingScan = useRef<boolean>(false);
  const scannedIdRef = useRef<string | null>(null);
  const scanSessionIdRef = useRef<string | null>(null);
  const isProcessingScanRef = useRef<boolean>(false);

  const extractQRParameter = useCallback((): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('qrcodescanned') ||
                       urlParams.get('qr') ||
                       urlParams.get('scan');

    if (searchParam) {
      console.log('[QR Scan] ✓ Found in URL search params:', searchParam);
      return searchParam;
    }

    const hash = window.location.hash;
    if (hash) {
      console.log('[QR Scan] Checking hash:', hash);
      const hashMatch = hash.match(/[#&](?:qrcodescanned|qr|scan)=([^&]+)/);
      if (hashMatch) {
        console.log('[QR Scan] ✓ Found in hash:', hashMatch[1]);
        return hashMatch[1];
      }
    }

    return null;
  }, []);

  const cleanURL = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('qrcodescanned');
    urlParams.delete('qr');
    urlParams.delete('scan');

    let newUrl = window.location.origin + window.location.pathname;
    if (urlParams.toString()) {
      newUrl += '?' + urlParams.toString();
    }

    let newHash = window.location.hash.replace(/[#&](?:qrcodescanned|qr|scan)=[^&]+&?/, '');
    if (newHash && newHash !== '#') {
      newUrl += newHash;
    }

    window.history.replaceState({}, '', newUrl);
    console.log('[QR Scan] 🧹 URL cleaned:', newUrl);
  }, []);

  // Only call data hooks when auth is fully loaded and user exists
  const startersUserId = !loading && user ? user.uid : undefined;
  const bakingSessionsUserId = !loading && user ? user.uid : undefined;

  logger.debug('Calling data hooks', {
    component: 'AppContent',
    loading,
    hasUser: !!user,
    startersUserId,
    bakingSessionsUserId,
    authCurrentUser: !!auth.currentUser
  });

  const { starters } = useStarters(startersUserId);
  const { bakingSessions, setBakingSessions } = useBakingSessions(bakingSessionsUserId);
  const { isSubscribed } = useSubscription();

  useEffect(() => {
    logger.info('Data loaded', {
      component: 'AppContent',
      startersCount: starters.length,
      bakingSessionsCount: bakingSessions.length,
      isSubscribed
    });
  }, [starters.length, bakingSessions.length, isSubscribed]);
  
  // Check if current user is admin
  const isAdmin = user?.email === 'breadflowbybadabec@gmail.com';
  const isReviewAccount = user?.email === 'review@breadlify.com';

  // Find active session for the BakingInProgressWidget
  useEffect(() => {
    if (bakingSessions.length > 0) {
      const now = new Date();
      // Find a session that is in progress
      const inProgressSession = bakingSessions.find(session => {
        if (session.status !== 'in-progress' && session.status !== 'scheduled') return false;
        
        // Get the fermentation process
        const process = FERMENTATION_PROCESSES.find(p => p.id === session.fermentationType);
        if (!process) return false;
        
        // Calculate kneading time
        const bakeTime = new Date(session.targetBakeTime);
        const kneadingTime = new Date(bakeTime);
        kneadingTime.setHours(kneadingTime.getHours() - process.totalTime);
        
        // Check if kneading time has passed but baking time hasn't
        return now > kneadingTime && now < bakeTime;
      });
      
      setActiveSession(inProgressSession || null);
    } else {
      setActiveSession(null);
    }
  }, [bakingSessions]);

  // Check subscription status on login
  useEffect(() => {
    logger.info('Subscription check effect triggered', {
      component: 'AppContent',
      hasUser: !!user,
      userId: user?.uid,
      isAdmin
    });

    // Clear subscription state immediately when user changes or logs out
    if (!user) {
      logger.debug('No user, clearing subscription state', { component: 'AppContent' });
      setShowSubscriptionBanner(false);
      setDaysLeftInTrial(null);
      setNotificationsChecked(false); // Reset notifications check on logout
      return;
    }

    const checkSubscription = async () => {
      try {
        logger.group('Checking subscription status');
        logger.info('Starting subscription check', {
          component: 'AppContent',
          userId: user.uid,
          isAdmin,
          authCurrentUser: !!auth.currentUser
        });

        // Clear stale data first
        setShowSubscriptionBanner(false);
        setDaysLeftInTrial(null);

        // Admin users always have full access, skip subscription checks
        if (isAdmin) {
          logger.info('Admin user detected, skipping subscription check', {
            component: 'AppContent',
            userId: user.uid
          });
          logger.groupEnd();
          return;
        }

        // This will update the subscription status if trial has expired
        logger.debug('Calling checkAndUpdateSubscriptionStatus', {
          component: 'AppContent',
          userId: user.uid
        });
        await checkAndUpdateSubscriptionStatus(user.uid);
        logger.debug('checkAndUpdateSubscriptionStatus completed', {
          component: 'AppContent',
          userId: user.uid
        });

        // Force server fetch to bypass cache and get fresh data
        logger.debug('Fetching user document from server', {
          component: 'AppContent',
          userId: user.uid
        });
        const userDoc = await getDocFromServer(doc(db, 'users', user.uid));

        if (userDoc.exists()) {
          const userData = userDoc.data();
          logger.info('User document fetched', {
            component: 'AppContent',
            userId: user.uid,
            subscriptionStatus: userData.subscriptionStatus,
            hasTrialEndDate: !!userData.trialEndDate,
            hasStripeCustomerId: !!userData.stripeCustomerId
          });

          // Handle free users with or without trial
          if (userData.subscriptionStatus === 'free') {
            if (userData.trialEndDate) {
              // User is on trial (free status with trial date)
              const trialEnd = userData.trialEndDate.toDate();
              const now = new Date();
              const diffTime = trialEnd.getTime() - now.getTime();
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

              // Show banner with days left (or 0 if expired)
              setDaysLeftInTrial(diffDays > 0 ? diffDays : 0);
              setShowSubscriptionBanner(true);
            } else {
              // Free user without trial
              setDaysLeftInTrial(0);
              setShowSubscriptionBanner(true);
            }
          }
          // Backward compatibility: Handle old trial users (paid status without Stripe)
          else if (userData.subscriptionStatus === 'paid' && userData.trialEndDate && !userData.stripeCustomerId) {
            const trialEnd = userData.trialEndDate.toDate();
            const now = new Date();
            const diffTime = trialEnd.getTime() - now.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Show banner for old trial users
            setDaysLeftInTrial(diffDays > 0 ? diffDays : 0);
            setShowSubscriptionBanner(true);
          }
        }
        logger.info('Subscription check completed successfully', {
          component: 'AppContent',
          userId: user.uid,
          showBanner: showSubscriptionBanner,
          daysLeft: daysLeftInTrial
        });
        logger.groupEnd();
      } catch (error) {
        logger.error('Error checking subscription', error, {
          component: 'AppContent',
          userId: user?.uid
        });
        logger.groupEnd();
      }
    };

    checkSubscription();
  }, [user]);

  // Check if user should see welcome modal on first login
  useEffect(() => {
    if (user) {
      const checkWelcomeStatus = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.hasSeenWelcome === false) {
              setShowWelcomeModal(true);
            }
          }
        } catch (error) {
          console.error('Error checking welcome status:', error);
        }
      };

      checkWelcomeStatus();
    }
  }, [user]);

  // Initialize notifications when user is verified
  useEffect(() => {
    if (user?.emailVerified) {
      const initNotifications = async () => {
        await initializeNotifications();
      };

      initNotifications().catch(console.error);
    }
  }, [user?.emailVerified]);

  // Check starters for notifications on login
  useEffect(() => {
    if (user && starters.length > 0 && !notificationsChecked) {
      console.log('Checking starters for feeding notifications on login...');

      // Notification creation moved to backend (checkStarterFeedings.ts)
      const checkStarters = async () => {
        console.log('Starter notification check on login is now handled by backend');
      };

      checkStarters();
      setNotificationsChecked(true);
    }
  }, [user, starters, notificationsChecked]);

  // Reset to home page on login
  useEffect(() => {
    if (user) {
      setActiveTab('home');
    }
  }, [user]);

  // Check for dev/test mode parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('qrdev') === 'true' || urlParams.get('qrtest') === 'true') {
      console.log('[QR Scan] 🧪 Dev mode activated');
      setShowQRGenerator(true);
    }
  }, []);

  // Sync refs with state
  useEffect(() => {
    scannedIdRef.current = qrScanState.scannedId;
    scanSessionIdRef.current = scanSessionId.current;
    isProcessingScanRef.current = isProcessingScan.current;
  }, [qrScanState.scannedId]);

  // Detect QR code results from WebViewGold iOS
  useEffect(() => {
    console.log('[QR Scan] 🔍 Setting up QR detection...');
    console.log('[QR Scan] Current URL:', window.location.href);
    console.log('[QR Scan] Current state:', {
      scannedId: qrScanState.scannedId,
      scanSessionId: scanSessionId.current,
      lastProcessedQR: lastProcessedQR.current,
      isProcessingScan: isProcessingScan.current
    });

    if (isProcessingScan.current) {
      console.log('[QR Scan] ⏸️ Already processing a scan, skipping...');
      return;
    }

    const wasScanning = sessionStorage.getItem('qr-scanning');
    const scanTimestamp = sessionStorage.getItem('qr-scan-timestamp');
    const storedSessionId = sessionStorage.getItem('qr-scan-session-id');

    if (wasScanning) {
      console.log('[QR Scan] 📱 Detected return from scanner (session:', storedSessionId, ', timestamp:', scanTimestamp, ')');
    }

    const qrParam = extractQRParameter();

    if (qrParam) {
      if (storedSessionId && storedSessionId === scanSessionId.current && qrScanState.scannedId === qrParam) {
        console.log('[QR Scan] ⏭️ Same session already displayed, cleaning URL only');
        cleanURL();
        sessionStorage.removeItem('qr-scanning');
        sessionStorage.removeItem('qr-scan-timestamp');
        sessionStorage.removeItem('qr-scan-session-id');
        return;
      }

      console.log('[QR Scan] ✅ Processing QR code:', qrParam);

      isProcessingScan.current = true;

      const newSessionId = `scan-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      scanSessionId.current = newSessionId;
      lastProcessedQR.current = qrParam;

      console.log('[QR Scan] 🆔 New scan session:', newSessionId);

      sessionStorage.removeItem('qr-scanning');
      sessionStorage.removeItem('qr-scan-timestamp');
      sessionStorage.removeItem('qr-scan-session-id');

      cleanURL();

      setShowInstantFeedback(true);

      dispatchQRScan({ type: 'SCAN_SUCCESS', payload: qrParam });
      isProcessingScan.current = false;

    } else if (wasScanning) {
      const timestamp = scanTimestamp ? parseInt(scanTimestamp) : 0;
      const elapsed = Date.now() - timestamp;

      console.log('[QR Scan] ❌ No QR parameter found after', elapsed, 'ms');

      if (elapsed > 500) {
        console.log('[QR Scan] User likely cancelled scan');
        sessionStorage.removeItem('qr-scanning');
        sessionStorage.removeItem('qr-scan-timestamp');
        sessionStorage.removeItem('qr-scan-session-id');
        dispatchQRScan({ type: 'SCAN_CANCEL' });
      }
    }

    const handleVisibilityChange = () => {
      if (!document.hidden && !isProcessingScanRef.current) {
        console.log('[QR Scan] 👁️ App became visible');
        const wasScanning = sessionStorage.getItem('qr-scanning');
        const storedSessionId = sessionStorage.getItem('qr-scan-session-id');

        if (wasScanning) {
          console.log('[QR Scan] Checking for scan result after visibility change...');
          const qrParam = extractQRParameter();
          if (qrParam) {
            if (storedSessionId && storedSessionId === scanSessionIdRef.current && scannedIdRef.current === qrParam) {
              console.log('[QR Scan] ⏭️ Same delayed session already displayed');
              cleanURL();
              sessionStorage.removeItem('qr-scanning');
              sessionStorage.removeItem('qr-scan-timestamp');
              sessionStorage.removeItem('qr-scan-session-id');
              return;
            }

            console.log('[QR Scan] ✅ Delayed detection found:', qrParam);

            isProcessingScan.current = true;
            isProcessingScanRef.current = true;

            const newSessionId = `scan-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            scanSessionId.current = newSessionId;
            scanSessionIdRef.current = newSessionId;
            lastProcessedQR.current = qrParam;

            console.log('[QR Scan] 🆔 New delayed scan session:', newSessionId);

            sessionStorage.removeItem('qr-scanning');
            sessionStorage.removeItem('qr-scan-timestamp');
            sessionStorage.removeItem('qr-scan-session-id');

            cleanURL();

            setShowInstantFeedback(true);

            dispatchQRScan({ type: 'SCAN_SUCCESS', payload: qrParam });
            isProcessingScan.current = false;
            isProcessingScanRef.current = false;
          } else {
            console.log('[QR Scan] ❌ No scan result detected - user cancelled');
            sessionStorage.removeItem('qr-scanning');
            sessionStorage.removeItem('qr-scan-timestamp');
            sessionStorage.removeItem('qr-scan-session-id');
            dispatchQRScan({ type: 'SCAN_CANCEL' });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    console.log('[QR Scan] ✓ Detection setup complete');

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      console.log('[QR Scan] 🧹 Detection cleanup complete');
    };
  }, [extractQRParameter, cleanURL, qrScanState.scannedId]);

  useEffect(() => {
    if (qrScanState.showSuccessModal && showInstantFeedback) {
      setShowInstantFeedback(false);
    }
  }, [qrScanState.showSuccessModal, showInstantFeedback]);

  const handleQRScan = () => {
    try {
      console.log('[QR Scan] 🚀 Starting QR scan process...');
      console.log('[QR Scan] Current state:', {
        status: qrScanState.status,
        scannedId: qrScanState.scannedId,
        showModal: qrScanState.showSuccessModal,
        currentSessionId: scanSessionId.current
      });

      const newScanSessionId = `scan-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      sessionStorage.setItem('qr-scanning', 'true');
      sessionStorage.setItem('qr-scan-timestamp', Date.now().toString());
      sessionStorage.setItem('qr-scan-session-id', newScanSessionId);
      console.log('[QR Scan] 💾 Saved scanning state to sessionStorage (session:', newScanSessionId, ')');

      dispatchQRScan({ type: 'SCAN_START' });
      isProcessingScan.current = false;

      console.log('[QR Scan] 📱 Opening WebViewGold scanner...');
      console.log('[QR Scan] 📝 QR codes should contain URL like: https://app.breadlify.com/?qrcodescanned=RECIPE_ID');

      window.location.href = 'qrcode://';

      console.log('[QR Scan] ✓ Scanner command sent');
    } catch (error) {
      console.error('[QR Scan] ✗ Error opening QR scanner:', error);
      sessionStorage.removeItem('qr-scanning');
      sessionStorage.removeItem('qr-scan-timestamp');
      sessionStorage.removeItem('qr-scan-session-id');
      dispatchQRScan({ type: 'SCAN_CANCEL' });
      isProcessingScan.current = false;
    }
  };

  const handleQRScanCancel = () => {
    console.log('[QR Scan] ❌ User cancelled scan');
    sessionStorage.removeItem('qr-scanning');
    sessionStorage.removeItem('qr-scan-timestamp');
    sessionStorage.removeItem('qr-scan-session-id');
    dispatchQRScan({ type: 'SCAN_CANCEL' });
    scanSessionId.current = null;
    isProcessingScan.current = false;
    console.log('[QR Scan] ✅ All state cleared after cancel');
  };

  const handleQRScanRetry = () => {
    console.log('[QR Scan] 🔄 Retrying scan...');
    handleQRScan();
  };

  const handleTestQRSuccess = () => {
    console.log('[QR Scan] 🧪 Testing success modal...');
    dispatchQRScan({ type: 'SCAN_SUCCESS', payload: 'test-recipe-123' });
  };

  const handleTabChange = (tab: string) => {
    // WebViewGold iOS and Safari iOS/iPad users always have access (App Store compliance)
    const isFreeOnIOS = shouldBypassSubscription();

    if (tab === 'new' && !isSubscribed && !isAdmin && !isFreeOnIOS) {
      setShowSubscriptionModal(true);
    } else if (tab === 'new') {
      setShowNewBake(true);
    } else if ((tab === 'challenges' || tab === 'analysis' || tab === 'knowledge') && !isSubscribed && !isAdmin && !isFreeOnIOS) {
      setShowSubscriptionModal(true);
    } else {
      setActiveTab(tab);
    }
  };

  const handleNewBakeSubmit = async (sessionData: any) => {
    // Guard against calls when auth is not ready
    if (!user || loading) {
      console.error('Cannot create baking session: auth not ready');
      return;
    }

    try {
      await addBakingSession({
        ...sessionData,
        userId: user.uid
      });
      setShowNewBake(false);
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const handleSessionCancel = async (sessionId: string) => {
    try {
      setBakingSessions(prev => prev.filter(session => session.id !== sessionId));
    } catch (error) {
      console.error('Error cancelling session:', error);
    }
  };

  const handleKnowledgeClick = () => {
    setActiveTab('knowledge');
  };

  const handleWelcomeClose = async () => {
    // Guard against calls when auth is not ready
    if (!user || loading) {
      console.error('Cannot update welcome status: auth not ready');
      setShowWelcomeModal(false);
      return;
    }

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        hasSeenWelcome: true
      });
      setShowWelcomeModal(false);
    } catch (error) {
      console.error('Error updating welcome status:', error);
      setShowWelcomeModal(false);
    }
  };

  const handleNewStarter = async (starterData: any) => {
    // Guard against calls when auth is not ready
    if (!user || loading) {
      console.error('Cannot create starter: auth not ready');
      return;
    }

    try {
      await addStarter({
        ...starterData,
        userId: user.uid
      });
      setShowNewStarter(false);
    } catch (error) {
      console.error('Error creating starter:', error);
    }
  };

  if (loading) {
    logger.info('Auth loading, showing spinner', {
      component: 'AppContent',
      authCurrentUser: !!auth.currentUser
    });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    logger.error('Auth error', error, {
      component: 'AppContent',
      errorMessage: error.message
    });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">Error: {error.message}</div>
      </div>
    );
  }

  if (!user) {
    logger.info('No user, showing login screen', {
      component: 'AppContent',
      authCurrentUser: !!auth.currentUser
    });
    return (
      <div 
        className="min-h-screen flex items-center justify-center relative overflow-hidden"
        style={{
          backgroundImage: 'url(/background.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative text-center space-y-6 max-w-lg mx-auto px-4">
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-6xl text-white tracking-tight font-playfair">
              .Breadlify
            </h1>
          </div>
          <p className="text-sm text-gray-200 leading-relaxed">
            Your sourdough baking companion.
          </p>
          <div className="flex flex-col gap-4 justify-center items-center">
            <button
              onClick={() => {
                setAuthMode('login');
                setShowAuth(true);
              }}
              className="w-[250px] px-6 py-2.5 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white rounded-2xl transition-all duration-300"
            >
              Log In
            </button>
            <button
              onClick={() => {
                setAuthMode('signup');
                setShowAuth(true);
              }}
              className="w-[250px] px-6 py-2 border border-white text-white rounded-2xl hover:bg-white/10 transition-colors duration-200"
            >
              Sign Up
            </button>
          </div>
        </div>

        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          mode={authMode}
          onSuccess={() => {
            setShowAuth(false);
            setNotificationsChecked(false); // Reset notifications check on new login
          }}
        />

        <InstantScanFeedback isVisible={showInstantFeedback} />

        <QRScanSuccessModal
          isOpen={qrScanState.showSuccessModal}
          scannedId={qrScanState.scannedId || undefined}
          onClose={() => {
            console.log('[QR Scan] 🔄 Modal closed - resetting all QR state');
            console.log('[QR Scan] Previous state:', {
              scannedId: qrScanState.scannedId,
              sessionId: scanSessionId.current,
              lastProcessedQR: lastProcessedQR.current
            });

            sessionStorage.removeItem('qr-scanning');
            sessionStorage.removeItem('qr-scan-timestamp');
            sessionStorage.removeItem('qr-scan-session-id');

            dispatchQRScan({ type: 'RESET_SCAN' });
            lastProcessedQR.current = null;
            scanSessionId.current = null;
            isProcessingScan.current = false;

            console.log('[QR Scan] ✅ All QR state cleared - ready for next scan');
          }}
        />

        <QRScanStatusOverlay
          status={qrScanState.status}
          onCancel={handleQRScanCancel}
          onRetry={handleQRScanRetry}
          onTestSuccess={handleTestQRSuccess}
        />

        <QRCodeGeneratorModal
          isOpen={showQRGenerator}
          onClose={() => setShowQRGenerator(false)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#4D4D4D] to-[#FFFFFF] text-gray-900">
      <Header
        user={user}
        onAuthClick={() => {
          setAuthMode('login');
          setShowAuth(true);
        }}
        onShowSubscriptionModal={() => {
          if (!isAdmin) {
            setShowSubscriptionModal(true);
          }
        }}
        onQRScan={handleQRScan}
      />
      
      {!user.emailVerified && (
        <EmailVerificationBanner user={user} />
      )}

      <NotificationErrorBanner />

      <div className="flex pt-16">
        {/* Subscription Banner - Hidden on WebViewGold iOS and Safari iOS/iPad (App Store compliance) */}
        {!isReviewAccount && !shouldBypassSubscription() && showSubscriptionBanner && daysLeftInTrial !== null && (
          <div className="fixed top-16 left-0 right-0 z-30 md:pl-[66px]">
            <SubscriptionBanner
              daysLeft={daysLeftInTrial}
              onClose={() => setShowSubscriptionBanner(false)}
              onUpgrade={() => setShowSubscriptionModal(true)}
            />
          </div>
        )}

        {/* Desktop Navigation */}
        <div className="hidden md:block w-[66px] min-h-[calc(100vh-4rem)]">
          <Navigation 
            activeTab={activeTab} 
            onTabChange={handleTabChange}
            layout="vertical"
          />
        </div>

        {/* Main Content */}
        <main className="flex-1 p-4 pb-24 md:pb-4">
          {activeTab === 'home' && (
            <Home
              bakingSessions={bakingSessions}
              starters={starters}
              onSessionCancel={handleSessionCancel}
              user={user}
              onNavigateToStarter={() => setActiveTab('starter')}
              onOpenHelpCenter={() => setShowHelpCenter(true)}
            />
          )}
          {activeTab === 'starter' && (
            <StarterPage 
              starters={starters}
              onUpdateStarter={() => {}}
            />
          )}
          {activeTab === 'challenges' && (
            <PremiumFeature featureName="Challenges">
              <ChallengesPage />
            </PremiumFeature>
          )}
          {activeTab === 'analysis' && (
            <PremiumFeature featureName="Bread Analysis">
              <AnalysisPage 
                onKnowledgeClick={handleKnowledgeClick}
              />
            </PremiumFeature>
          )}
          {activeTab === 'knowledge' && (
            <KnowledgePage />
          )}
        </main>
      </div>

      {/* Mobile Navigation */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden">
        <Navigation 
          activeTab={activeTab} 
          onTabChange={handleTabChange}
          layout="horizontal"
        />
      </div>

      <NewBakeModal
        isOpen={showNewBake}
        onClose={() => setShowNewBake(false)}
        onSubmit={handleNewBakeSubmit}
        starters={starters}
        existingSessions={bakingSessions}
      />

      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        mode={authMode}
        onSuccess={() => {
          setShowAuth(false);
          setNotificationsChecked(false); // Reset notifications check on new login
        }}
      />
        
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />

      {showWelcomeModal && (
        <WelcomeModal
          onClose={handleWelcomeClose}
          onCreateStarter={async () => {
            await handleWelcomeClose();
            setShowNewStarter(true);
          }}
          onPlanBake={async () => {
            await handleWelcomeClose();
            setShowNewBake(true);
          }}
        />
      )}

      <NewStarterModal
        isOpen={showNewStarter}
        onClose={() => setShowNewStarter(false)}
        onSubmit={handleNewStarter}
      />

      <HelpCenterModal
        isOpen={showHelpCenter}
        onClose={() => setShowHelpCenter(false)}
      />

      <InstantScanFeedback isVisible={showInstantFeedback} />

      <QRScanSuccessModal
        isOpen={qrScanState.showSuccessModal}
        scannedId={qrScanState.scannedId || undefined}
        onClose={() => {
          console.log('[QR Scan] 🔄 Modal closed - resetting all QR state');
          console.log('[QR Scan] Previous state:', {
            scannedId: qrScanState.scannedId,
            sessionId: scanSessionId.current,
            lastProcessedQR: lastProcessedQR.current
          });

          sessionStorage.removeItem('qr-scanning');
          sessionStorage.removeItem('qr-scan-timestamp');
          sessionStorage.removeItem('qr-scan-session-id');

          dispatchQRScan({ type: 'RESET_SCAN' });
          lastProcessedQR.current = null;
          scanSessionId.current = null;
          isProcessingScan.current = false;

          console.log('[QR Scan] ✅ All QR state cleared - ready for next scan');
        }}
      />

      <QRScanStatusOverlay
        status={qrScanState.status}
        onCancel={handleQRScanCancel}
        onRetry={handleQRScanRetry}
        onTestSuccess={handleTestQRSuccess}
      />

      <QRCodeGeneratorModal
        isOpen={showQRGenerator}
        onClose={() => setShowQRGenerator(false)}
      />

      {/* Baking In Progress Widget */}
      {activeSession && (
        <BakingInProgressWidget session={activeSession} />
      )}

      {/* WebViewGold Debug (only visible on iOS WebViewGold) */}
      <WebViewGoldDebug />
    </div>
  );
}

function App() {
  return (
    <SubscriptionProvider>
      <AppContent />
    </SubscriptionProvider>
  );
}

export default App;